import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AppCanvas } from "@/components/glass/AppCanvas";
import { resetPassword } from "@/lib/api";
import orikinWordmark from "@/assets/brand/orikin-wordmark-on-dark.png";
import "@/pages/login.css";

// §v3.pw-reset — the page the emailed reset link lands on:
// /reset-password?token=… → set a new password. The token is single-use
// and time-limited (validated server-side); the server also enforces the
// real password-strength policy and returns its message on a weak one.

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    if (!token) {
      setError(
        "This reset link is missing its token. Please use the link from your email.",
      );
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      window.setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      // Surfaces server messages: expired/used token, weak password, etc.
      setError(err instanceof Error ? err.message : "Couldn't reset the password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lp-stage">
      <AppCanvas variant="login" />
      <form className="lp-card" onSubmit={handleSubmit} noValidate>
        <header className="lp-head">
          <img
            src={orikinWordmark}
            alt="ORIKIN AI"
            className="lp-wordmark"
            draggable={false}
          />
          <div className="lp-tagline">SET NEW PASSWORD</div>
        </header>

        <div className="lp-form">
          {done ? (
            <div role="status" className="lp-success">
              Password reset successfully. Taking you to sign in…
            </div>
          ) : (
            <>
              <div className="lp-field">
                <label htmlFor="new-password" className="lp-label">
                  New password
                </label>
                <input
                  id="new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="lp-input"
                />
              </div>
              <div className="lp-field">
                <label htmlFor="confirm-password" className="lp-label">
                  Confirm new password
                </label>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="lp-input"
                />
              </div>
              {error ? (
                <div role="alert" className="lp-error">
                  {error}
                </div>
              ) : null}
              <button
                type="submit"
                className="lp-submit"
                disabled={submitting}
                aria-busy={submitting || undefined}
              >
                {submitting ? (
                  <>
                    <span className="lp-submit-spinner" aria-hidden="true" />
                    Resetting…
                  </>
                ) : (
                  "Reset password"
                )}
              </button>
            </>
          )}

          <Link
            to="/login"
            className="lp-bootstrap-toggle"
            style={{ textAlign: "center", display: "block" }}
          >
            ← Back to sign in
          </Link>
        </div>
      </form>
    </div>
  );
}

export default ResetPasswordPage;
